import React from 'react';
import classNames from 'classnames';
import type { Colors } from 'src/common/types/Colors';
import type { Spacing } from 'src/common/types/Spacing';
import getClassNames from 'src/common/utils/getClassNames';
import styles from './DesignSystemStyles.module.scss';

/**
 * Props for adding design system styling to components
 * @typedef {Object} DesignSystemProps
 * @property {string} [className] - Component specific CSS class names to apply
 * @property {'center'|'end'|'normal'|'start'} [align] - Alignment for flex items
 * (maps to align-items CSS property)
 * @property {Colors} [background] - Background color from the design system colors
 * @property {'1'|'2'|'3'|'4'|'rounded'} [borderRadius] - Border radius size from the design system
 * @property {'column'|'row'} [direction] - Flex direction for the component's layout
 * @property {Spacing} [gapSize] - Gap size for flex/grid layouts from the design system spacing
 * @property {Spacing} [padding] - Padding on all sides from the design system spacing scale
 * @property {Spacing} [paddingTop] - Top padding from the design system spacing scale
 * @property {Spacing} [paddingRight] - Right padding from the design system spacing scale
 * @property {Spacing} [paddingBottom] - Bottom padding from the design system spacing scale
 * @property {Spacing} [paddingLeft] - Left padding from the design system spacing scale
 */
export type DesignSystemStylesProps = {
  className?: string;
  align?: 'center' | 'end' | 'normal' | 'start';
  background?: Colors;
  borderRadius?: '1' | '2' | '3' | '4' | 'rounded';
  direction?: 'column' | 'row';
  gapSize?: Spacing;
  padding?: Spacing;
  paddingTop?: Spacing;
  paddingRight?: Spacing;
  paddingBottom?: Spacing;
  paddingLeft?: Spacing;
};

/**
 * Higher Order Component that adds design system props to any component
 *
 * This HOC injects standardized design system classes for common styling needs
 * like alignment, background colors, flex direction, spacing, padding, and border radius,
 * making it easier to maintain consistent styling across the application.
 *
 * @template P - The props type of the wrapped component
 * @param {React.ComponentType<P>} Component - The component to wrap
 * @returns {React.FC<P & DesignSystemStylesProps>} - The enhanced component with design
 * system styles props
 *
 * @example
 * // Basic usage
 * const EnhancedDiv = withDesignSystem(({ className, children }) => (
 *   <div className={className}>{children}</div>
 * ));
 *
 * // Usage in a component
 * <EnhancedDiv
 *   align="center"
 *   direction="column"
 *   background="neutral-0"
 *   padding="spacing-05"
 *   borderRadius="2"
 * >
 *   Content with design system styling
 * </EnhancedDiv>
 */
export const withDesignSystemStyles = <P extends object>(
  Component: React.ComponentType<P>,
): React.FC<P & DesignSystemStylesProps & { className?: string }> => {
  const WithDesignSystemStyles: React.FC<P & DesignSystemStylesProps & { className?: string }> = ({
    className,
    align,
    background,
    borderRadius,
    direction = 'column',
    gapSize,
    padding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    ...props
  }) => {
    const designSystemClasses = getClassNames(styles, 'root', classNames(
      align && `align-${align}`,
      background && `background-${background}`,
      borderRadius && `border-radius-${borderRadius}`,
      direction && `direction-${direction}`,
      gapSize && `gap-${gapSize}`,
      padding && `padding-${padding}`,
      paddingTop && `padding-top-${paddingTop}`,
      paddingRight && `padding-right-${paddingRight}`,
      paddingBottom && `padding-bottom-${paddingBottom}`,
      paddingLeft && `padding-left-${paddingLeft}`,
    ));

    return (
      <Component className={classNames(className, designSystemClasses)} {...props as P} />
    );
  };

  WithDesignSystemStyles.displayName = `withDesignSystemStyles(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WithDesignSystemStyles;
};
